/* eslint-disable max-lines */
import React, {
    useMemo, useState, useRef, useEffect, useCallback
} from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';
import {
    Input
} from 'shared/components/ui/input';
import {
    Search, Check, Loader2, RefreshCw, AlertCircle
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    modelService
} from 'shared/api/services/BEX/models.services';
import './ModelCatalogModal.css';

/**
 * ModelCatalogModal — table picker over the full model registry.
 *
 * Fetches the LIVE catalog from the server (`POST /llm/models/get-llm-models`
 * via `modelService`) with `provider: ['All']`. The server resolves `All` to
 * the org's enabled providers from MongoDB, so the catalog respects admin toggles.
 */

const MODEL_REQUEST = {
    provider: ['All'],
    filter: {
        Watson: { region: 'All', showInactive: true },
        Groq: { showInactive: true },
        SambaNova: { showInactive: true },
        OnPrem: { showInactive: true },
        Gemini: { showInactive: true },
        Cerebras: { showInactive: true },
        Mistral: { showInactive: true },
        Novita: { showInactive: true },
    },
};

export default function ModelCatalogModal({
    open, onOpenChange, currentModelId, currentProviderType, onPick, organizationId,
}) {
    const [query, setQuery] = useState('');
    const [provider, setProvider] = useState('all');
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const searchRef = useRef(null);

    const fetchModels = useCallback(async (hardRefresh = false) => {
        setLoading(true);
        setError(null);
        try {
            const res = await modelService.getModels(MODEL_REQUEST, hardRefresh, organizationId);
            setModels(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            setError(
                err?.response?.data?.message
                || err?.response?.data?.error
                || err?.message
                || 'Failed to load models.'
            );
            setModels([]);
        } finally {
            setLoading(false);
        }
    }, [organizationId]);

    const allProviders = useMemo(() => {
        const set = new Set(models.map(m => m.provider_type).filter(Boolean));
        return Array.from(set).sort();
    }, [models]);

    const activeProviderSet = useMemo(() => new Set(
        models.filter(m => m.active !== false).map(m => m.provider_type).filter(Boolean)
    ), [models]);

    const activeTotal = useMemo(
        () => models.filter(m => m.active !== false || m.deprecated === true).length,
        [models]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return models
            .filter(m => m.active !== false || m.deprecated === true)
            .filter(m => provider === 'all' || m.provider_type === provider)
            .filter(m => {
                if (!q) return true;
                const hay = [
                    m.model_id, m.owned_by, m.provider_type,
                    ...(m.tasks || []), m.description || '',
                ].join(' ').toLowerCase();
                return hay.includes(q);
            });
    }, [query, provider, models]);

    useEffect(() => {
        if (open) {
            if (models.length === 0) fetchModels(false);
            const t = setTimeout(() => searchRef.current?.focus(), 50);
            return () => clearTimeout(t);
        }
        setQuery('');
        setProvider('all');
        return undefined;
    }, [open]);

    function pick(m) {
        onPick({
            model_id: m.model_id,
            provider_type: m.provider_type,
            owned_by: m.owned_by,
            context_window: m.context_window,
            tasks: m.tasks || [],
            description: m.description || '',
        });
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl gap-4">
                <DialogHeader>
                    <DialogTitle>Pick a model</DialogTitle>
                    <DialogDescription>
                        Browse available models by provider. Selecting a row sets the model and provider.
                    </DialogDescription>
                </DialogHeader>

                <div className="model-catalog-modal">
                    <div className="model-catalog-toolbar">
                        <div className="model-catalog-search-wrap">
                            <Input
                                ref={searchRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                placeholder="Search by model, provider, or description…"
                            />
                            <Search aria-hidden />
                        </div>
                        <div className="model-catalog-filters" role="tablist" aria-label="Filter by provider">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={provider === 'all'}
                                onClick={() => setProvider('all')}
                                className={cn('model-catalog-filter', provider === 'all' && 'model-catalog-filter--active')}
                            >
                                All
                            </button>
                            {allProviders.map(p => {
                                const isActive = activeProviderSet.has(p);
                                return (
                                    <button
                                        key={p}
                                        type="button"
                                        role="tab"
                                        aria-selected={provider === p}
                                        disabled={!isActive}
                                        title={!isActive ? `${p} — not available` : undefined}
                                        onClick={() => isActive && setProvider(p)}
                                        className={cn(
                                            'model-catalog-filter',
                                            provider === p && 'model-catalog-filter--active'
                                        )}
                                    >
                                        {p}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="model-catalog-meta">
                        <span>
                            {loading ? 'Loading models…' : `${filtered.length} of ${activeTotal} models`}
                        </span>
                        <button
                            type="button"
                            onClick={() => fetchModels(true)}
                            disabled={loading}
                            className="model-catalog-refresh"
                        >
                            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                            Refresh
                        </button>
                    </div>

                    <div className="model-catalog-table-wrap">
                        {loading ? (
                            <TableSkeleton />
                        ) : error ? (
                            <div className="model-catalog-empty">
                                <div className="mb-2 flex items-center justify-center gap-1.5 text-destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    {error}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => fetchModels(false)}
                                    className="model-catalog-filter"
                                >
                                    Retry
                                </button>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="model-catalog-empty">No models match your search.</div>
                        ) : (
                            <table className="model-catalog-table">
                                <thead>
                                    <tr>
                                        <th className="model-catalog-col-model">Model</th>
                                        <th className="model-catalog-col-provider">Provider</th>
                                        <th className="model-catalog-col-owner">Owner</th>
                                        <th className="model-catalog-col-context">Context</th>
                                        <th className="model-catalog-col-capabilities">Capabilities</th>
                                        <th className="model-catalog-col-desc">Description</th>
                                        <th className="model-catalog-col-action" style={{ textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((m, i) => {
                                        const sel = m.model_id === currentModelId
                                            && m.provider_type === currentProviderType;
                                        const isDeprecated = m.deprecated === true;
                                        return (
                                            <tr
                                                key={`${m.provider_type}:${m.model_id}:${i}`}
                                                className={cn(
                                                    sel && 'model-catalog-row--selected',
                                                    isDeprecated && 'model-catalog-row--deprecated',
                                                )}
                                            >
                                                <td className="model-catalog-col-model">
                                                    <div className="model-catalog-model-cell" title={m.model_id}>
                                                        <span className="model-catalog-model-id">{m.model_id}</span>
                                                        {isDeprecated && (
                                                            <span className="model-catalog-tag model-catalog-tag--deprecated">
                                                                Deprecated
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="model-catalog-col-provider">{m.provider_type || '—'}</td>
                                                <td className="model-catalog-col-owner model-catalog-muted">{m.owned_by || '—'}</td>
                                                <td className="model-catalog-col-context model-catalog-muted">
                                                    {typeof m.context_window === 'number'
                                                        ? formatContext(m.context_window)
                                                        : '—'}
                                                </td>
                                                <td className="model-catalog-col-capabilities model-catalog-capabilities" title={(m.tasks || []).join(', ') || undefined}>
                                                    {(m.tasks?.length ?? 0) > 0
                                                        ? m.tasks.map(t => t.replace(/_/g, ' ')).join(', ')
                                                        : '—'}
                                                </td>
                                                <td
                                                    className="model-catalog-col-desc model-catalog-desc"
                                                    title={m.description || undefined}
                                                >
                                                    {m.description || '—'}
                                                </td>
                                                <td className="model-catalog-col-action model-catalog-action-cell">
                                                    {sel ? (
                                                        <span className="model-catalog-select-btn model-catalog-select-btn--selected">
                                                            <Check className="h-3 w-3" aria-hidden />
                                                            Selected
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="model-catalog-select-btn"
                                                            onClick={() => pick(m)}
                                                        >
                                                            Select
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TableSkeleton() {
    return (
        <table className="model-catalog-table">
            <thead>
                <tr>
                    <th>Model</th>
                    <th>Provider</th>
                    <th>Owner</th>
                    <th>Context</th>
                    <th>Capabilities</th>
                    <th>Description</th>
                    <th style={{ width: 88 }} />
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: 8 }, (_, i) => (
                    <tr key={i} aria-hidden>
                        <td><div className="model-catalog-skeleton animate-pulse" style={{ width: '72%' }} /></td>
                        <td><div className="model-catalog-skeleton animate-pulse" style={{ width: 48 }} /></td>
                        <td><div className="model-catalog-skeleton animate-pulse" style={{ width: 64 }} /></td>
                        <td><div className="model-catalog-skeleton animate-pulse" style={{ width: 32 }} /></td>
                        <td><div className="model-catalog-skeleton animate-pulse" style={{ width: '80%' }} /></td>
                        <td><div className="model-catalog-skeleton animate-pulse" style={{ width: '90%' }} /></td>
                        <td><div className="model-catalog-skeleton animate-pulse" style={{ width: 56, marginLeft: 'auto' }} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function formatContext(n) {
    if (n >= 1024) return `${Math.round(n / 1024)}K`;
    return String(n);
}
